import { useCallback, useState, useEffect } from "react";
import {
  Box,
  Flex,
  Heading,
  Link,
  List,
  ListItem,
  Text,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { DollarSign, Flag, LayoutGrid, ArrowDown } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { getCurrentColor, headerLink, bounceY } from "../../constants";
import MainPageFreeAmmo from "./MainPageFreeAmmo";
import useCookie from "../../hooks/useCookie";
import { data } from "../../data";
import { useAtom } from "jotai";
import { selectedCaliberAtom } from "../../atom/calibers";

const MainPageDiscoverSection = () => {
  const { colorMode } = useColorMode();
  const { cookies } = useCookie();
  const [, setSelectedCaliber] = useAtom(selectedCaliberAtom);
  const [showHint, setShowHint] = useState(false);
  const [hideHint, setHideHint] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => setShowHint(true), 600);
    const hideTimer = setTimeout(() => setHideHint(true), 10000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  const renderInfo = useCallback(
    (data) => {
      const { category, subCategories, categoryLink } = data;

      return (
        <>
          <Heading
            key={category}
            as="h3"
            fontSize="2xl"
            color={getCurrentColor(colorMode, "blackC.200", "whiteC.200")}
            fontFamily="mainFont"
            fontWeight="600"
            mb={2}
            textAlign="start"
          >
            {category}
          </Heading>

          <Flex
            gap={{ base: 0, sm: 2 }}
            flexDirection={{ base: "column", sm: "row" }}
          >
            <List
              display="flex"
              gap="5px"
              flexWrap="wrap"
              justifyContent="start"
              mb={{ base: "5px", md: "16px" }}
              flexDirection="column"
            >
              {subCategories.map(({ name, url }) => {
                return (
                  <ListItem key={name}>
                    <Link
                      as={RouterLink}
                      to={url}
                      state={headerLink(
                        // eslint-disable-next-line
                        category.toLowerCase().replace("/(?:s|-)?ammo/gi,", ""),
                        name,
                        cookies.zip,
                      )}
                      fontSize="16px"
                      fontFamily="mainFont"
                      fontWeight="500"
                      color={getCurrentColor(
                        colorMode,
                        "blackC.200",
                        "whiteC.200",
                      )}
                      borderBottom="1px solid"
                      borderColor={getCurrentColor(
                        colorMode,
                        "blackC.200",
                        "whiteC.200",
                      )}
                      onClick={() => {
                        localStorage.setItem("selectedCaliber", name);
                        setSelectedCaliber(name);
                      }}
                      _hover={{ textDecoration: "none", border: "none" }}
                      wordBreak="normal"
                    >
                      <ArrowForwardIcon mr={{ base: 1, md: 2 }} />
                      {name}
                    </Link>
                  </ListItem>
                );
              })}
            </List>
          </Flex>

          <Link
            as={RouterLink}
            // eslint-disable-next-line
            to={`/${categoryLink.toLowerCase()}`}
            color={"blueC.100"}
            fontWeight="500"
            fontFamily="mainFont"
            fontSize="md"
            mt="auto"
            display={"flex"}
            w={"100%"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Text as="span" display={"inline-block"}>
              Shop {category}
            </Text>{" "}
            <ArrowForwardIcon ml={2} />
          </Link>
        </>
      );
    },
    // eslint-disable-next-line
    [colorMode],
  );

  const InfoBlock = ({ icon: Icon, title, description }) => {
    const iconBg = useColorModeValue("blue.50", "blue.900");
    const iconColor = useColorModeValue("blue.400", "blue.300");
    const mutedText = useColorModeValue("gray.500", "gray.400");

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={3}
        textAlign="center"
      >
        {/* Icon Circle */}
        <Box
          w="44px"
          h="44px"
          borderRadius="full"
          bg={iconBg}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box color={iconColor} display="flex">
            <Icon size={20} />
          </Box>
        </Box>

        {/* Text Content */}
        <Box>
          <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="bold">
            {title}
          </Text>

          <Text fontSize="sm" fontWeight="bold" color={mutedText}>
            {description}
          </Text>
        </Box>
      </Box>
    );
  };

  return (
    <Flex
      maxW="1240px"
      w="100%"
      px={{ base: "22px", xl: "10px" }}
      pt={{ base: 8, lg: 6 }}
      mx="auto"
      flexDirection="column"
      mb={{ base: "50px", lg: "60px" }}
    >
      {/* Scroll hint — appears after 600ms, disappears after 10s */}
      {showHint && !hideHint && (
        <Box position="relative" textAlign="center">
          <Link
            href="#giveaway-banner"
            position={{ base: "relative", sm: "absolute" }}
            right={{ sm: 0 }}
            top={{ sm: 3 }}
            display="inline-flex"
            alignItems="center"
            gap={1.5}
            bg="blue.400"
            color="white"
            fontSize={{ base: "10px", sm: "xs" }}
            fontWeight="bold"
            px={{ base: 3, sm: 4 }}
            py={{ base: 1.5, sm: 2 }}
            borderRadius="lg"
            boxShadow="lg"
            mb={{ base: 3, sm: 0 }}
            _hover={{ bg: "blue.500", textDecoration: "none" }}
            transition="all 0.2s"
            whiteSpace="nowrap"
            zIndex={50}
          >
            <Box
              as={ArrowDown}
              h="12px"
              w="12px"
              animation={`${bounceY} 1s ease-in-out infinite`}
            />
            Scroll Down for Giveaway
          </Link>
        </Box>
      )}

      <Heading
        as="h2"
        fontSize={{ base: "3xl", sm: "4xl", md: "5xl", lg: "6xl" }}
        color={getCurrentColor(colorMode, "blackC.200", "whiteC.100")}
        fontFamily="mainFont"
        fontWeight="700"
        mb={{ base: 2, md: 4 }}
        textAlign="center"
      >
        Welcome to{" "}
        <Text as="span" color="blue.400">
          BulletScout
        </Text>
      </Heading>
      <Text
        color={getCurrentColor(colorMode, "grayC.100", "grayC.1600")}
        fontSize={{ base: "md", sm: "lg", md: "xl" }}
        fontFamily="mainFont"
        fontWeight="500"
        mb={{ base: 8, md: 12 }}
        textAlign="center"
        maxW="2xl"
        mx="auto"
        px={2}
      >
        Buying ammo online shouldn't require opening ten tabs, guessing shipping
        costs, or getting burned at checkout. BulletScout is a free ammo price
        comparison search engine built for shooters who want to see the real,
        delivered cost per round before they buy.
      </Text>

      <Flex
        direction={{ base: "column", sm: "row" }}
        gap={{ base: 6, sm: 8 }}
        mb={{ base: 10, md: 14 }}
        justify="center"
        maxW="7xl"
        mx="auto"
        w="full"
        px={{ base: 2, sm: 6 }}
      >
        <InfoBlock
          icon={DollarSign}
          title="True Price Per Round"
          description="Shipping included In CPR."
        />
        <InfoBlock
          icon={LayoutGrid}
          title="Organized by Product"
          description="No more cluttered listings."
        />
        <InfoBlock
          icon={Flag}
          title="Patriot Made"
          description="Built by Americans who shoot straight."
        />
      </Flex>

      <Flex flexWrap={{ base: "wrap", xl: "nowrap" }} mb={2} gap="20px">
        {data.map((item, index) => {
          if (item.category === "Brands") return null;
          return (
            <Flex
              key={index}
              borderRadius="10px"
              w={{ base: "100%", md: "48%", xl: "25%" }}
              minH="350px"
              px="20px"
              py="15px"
              border="1px solid"
              borderColor={getCurrentColor(
                colorMode,
                "grayC.500",
                "transparent",
              )}
              bg={getCurrentColor(colorMode, "blueC.800", "grayC.1700")}
              position="relative"
            >
              <Flex
                flexDirection="column"
                maxW={index % 2 === 0 ? "408px" : "411px"}
                w="100%"
              >
                {renderInfo(item)}
              </Flex>
            </Flex>
          );
        })}
      </Flex>

      <Box id="giveaway-banner">
        <MainPageFreeAmmo />
      </Box>
    </Flex>
  );
};

export default MainPageDiscoverSection;
